'use client';

import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import ResolvingLeftIcon from '@/assets/icons/account/resolving-left.svg';
import ResolvingRightIcon from '@/assets/icons/account/resolving-right.svg';
import EyeSmallIcon from '@/assets/icons/eye-small.svg';
import OngoingIcon from '@/assets/icons/ongoing.svg';
import { Image } from '@/components/shared/Image';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { C_Regexp } from '@/ts/constants/C_Regexp';

export type T_TransferHistoryCard = {
  tokenIconUrl?: string;
  tokenIconAlt?: string;
  Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  predictedPrice?: string;
  tokenSymbol?: string;
  showPredictionsLabel?: boolean;
  dateTime: string;
  status: 'ongoing' | 'resolving' | 'won' | 'lost' | 'both' | 'canceled';
  wonAmountText?: string;
  lostAmountText?: string;
  refundAmountText?: string;
  isClaimWin: boolean;
  isClaimRefund?: boolean;
  isClaimed?: boolean;
  isClaimedRefund?: boolean;
  isClaiming?: boolean;
  showEyeIcon?: boolean;
  onClaimWin?: () => void;
  onClaimRefund?: () => void;
  onViewWinners?: () => void;
  onViewMissedPrediction?: () => void;
  onViewOngoing?: () => void;
};

function TransferHistoryCard({
  tokenIconUrl,
  tokenIconAlt,
  Icon,
  predictedPrice,
  tokenSymbol,
  showPredictionsLabel,
  dateTime,
  status,
  wonAmountText,
  lostAmountText,
  refundAmountText,
  isClaimWin,
  isClaimRefund,
  isClaimed,
  isClaimedRefund,
  isClaiming,
  showEyeIcon,
  onClaimWin,
  onClaimRefund,
  onViewWinners,
  onViewMissedPrediction,
  onViewOngoing
}: T_TransferHistoryCard) {
  const t = useTranslations();

  // Remove "Tx" prefix from tokenSymbol if present
  const displayTokenSymbol = useMemo(() => {
    if (!tokenSymbol) return tokenSymbol;
    return tokenSymbol.replace(C_Regexp.txPrefix, '');
  }, [tokenSymbol]);

  const hasButtons =
    isClaimWin ||
    isClaimed ||
    isClaimRefund ||
    isClaimedRefund ||
    (status !== 'ongoing' && status !== 'resolving' && status !== 'canceled');

  return (
    <div>
      <div
        className={cn(
          'flex flex-col rounded-lg bg-accordion-brand',
          hasButtons && 'rounded-bl-none rounded-br-none'
        )}
      >
        <div className="flex w-full justify-between items-center p-4 min-w-46">
          <div className="mb-3 flex items-center">
            <div className="w-10 h-10 p-2 flex items-center justify-center rounded-md mr-3 bg-card">
              {tokenIconUrl ? (
                <Image
                  src={tokenIconUrl}
                  alt={tokenIconAlt || ''}
                  width={40}
                  height={40}
                  className="w-full h-full object-contain"
                />
              ) : Icon ? (
                <Icon />
              ) : null}
            </div>
            <div>
              {status === 'canceled' ? (
                // For canceled status, show only tokenSymbol
                <P className="mb-0.5 text-sm font-bold">
                  {displayTokenSymbol} {t('profile.transferHistory.prediction')}
                </P>
              ) : showPredictionsLabel ? (
                <>
                  <P className="mb-0.5 text-sm font-bold">
                    {displayTokenSymbol}
                  </P>
                  <P className="text-sm font-bold">
                    {t('profile.transferHistory.predictions')}
                  </P>
                </>
              ) : (
                <>
                  <P className="mb-0.5 text-sm font-bold">
                    {displayTokenSymbol}{' '}
                    {t('profile.transferHistory.prediction')}
                  </P>
                  {predictedPrice && (
                    <P className="text-sm font-bold">{predictedPrice}</P>
                  )}
                </>
              )}
            </div>
          </div>
          <P className="text-muted-foreground text-xs">{dateTime}</P>
        </div>

        <div className="w-full border-b-1 " />

        {status !== 'both' ? (
          <div className={cn('py-4 px-5 flex w-full justify-between')}>
            <div className="flex justify-between items-center gap-2.5 w-full">
              {status === 'ongoing' ? (
                <P
                  icon={<OngoingIcon />}
                  className="text-sm font-bold w-full justify-center"
                >
                  {t('profile.transferHistory.statuses.ongoing')}{' '}
                </P>
              ) : status === 'resolving' ? (
                <div className="flex items-center gap-2.5 w-full justify-center">
                  <ResolvingLeftIcon />
                  <P className="text-sm font-bold animate-pulse">
                    {t('profile.transferHistory.statuses.resolving')}{' '}
                  </P>
                  <ResolvingRightIcon />
                </div>
              ) : status === 'canceled' ? (
                <P className="text-sm font-bold w-full">
                  {t('profile.transferHistory.statuses.canceled')}{' '}
                </P>
              ) : status === 'won' ? (
                <P className="text-sm font-bold text-primary">
                  {t('profile.transferHistory.statuses.won')}{' '}
                </P>
              ) : (
                <P className="text-sm font-bold text-muted-foreground">
                  {t('profile.transferHistory.statuses.lost')}{' '}
                </P>
              )}
            </div>
            {(status === 'ongoing' || status === 'resolving') &&
              showEyeIcon && (
                <Button
                  variant="outline"
                  size="icon"
                  className="w-4 h-4"
                  icon={<EyeSmallIcon className="text-foreground" />}
                  onClick={onViewOngoing}
                />
              )}

            {status !== 'ongoing' &&
              status !== 'resolving' &&
              status !== 'canceled' && (
                <div className="mt-0.5">
                  {status === 'won' && wonAmountText ? (
                    <P className="text-primary text-sm font-bold">
                      {wonAmountText}
                    </P>
                  ) : status === 'lost' && lostAmountText ? (
                    <div className="flex justify-between w-full items-center gap-4">
                      <P className="text-muted-foreground text-sm font-bold">
                        {lostAmountText}
                      </P>
                      {showEyeIcon && (
                        <Button
                          variant="outline"
                          size="icon"
                          className="w-4 h-4"
                          icon={<EyeSmallIcon className="text-foreground" />}
                          onClick={onViewMissedPrediction}
                        />
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            {status === 'canceled' && refundAmountText && (
              <div className="mt-0.5">
                <P className="text-sm font-bold">{refundAmountText}</P>
              </div>
            )}
          </div>
        ) : (
          <div className="flex w-full items-center justify-between ">
            <div className="flex flex-col justify-center py-4 px-5 w-1/2 border-r-1">
              <P className="text-sm font-bold text-primary">
                {t('profile.transferHistory.statuses.won')}
              </P>
              <P className="text-primary text-sm font-bold">{wonAmountText}</P>
            </div>

            <div className="flex justify-between items-center w-1/2 py-4 px-5 ">
              <div className="flex justify-between items-center gap-2 w-full">
                <div className="flex flex-col">
                  <P className="text-sm font-bold text-muted-foreground">
                    {t('profile.transferHistory.statuses.lost')}
                  </P>
                  <P className="text-muted-foreground text-sm font-bold">
                    {lostAmountText}
                  </P>
                </div>
                <div className="flex items-center gap-2">
                  {showEyeIcon && status === 'both' && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="w-4 h-4"
                      icon={<EyeSmallIcon className="text-foreground" />}
                      onClick={onViewMissedPrediction}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex w-full items-center">
        {(isClaimWin || isClaimed) && (
          <Button
            onClick={onClaimWin}
            className="w-full rounded-t-none border-0 rounded-br-none active:border-0"
            variant="default"
            disabled={isClaiming || isClaimed}
          >
            {isClaimed
              ? t('profile.predictionWinners.claimed') || 'CLAIMED'
              : isClaiming
                ? t('profile.predictionWinners.claiming') || 'CLAIMING...'
                : t('profile.actions.claimWin')}
          </Button>
        )}
        {(isClaimRefund || isClaimedRefund) && (
          <Button
            onClick={onClaimRefund}
            className={cn(
              'w-full rounded-t-none border-0 active:border-0 bg-[var(--color-yellow)] hover:bg-[var(--color-yellow)]/90 text-foreground',
              'focus:ring-ring active:opacity-[var(--opacity-pressed)]',
              isClaimWin || isClaimed ? 'rounded-bl-none' : 'rounded-b-lg'
            )}
            variant="default"
            disabled={isClaiming || isClaimedRefund}
          >
            {isClaimedRefund
              ? t('profile.predictionWinners.claimed') || 'CLAIMED'
              : isClaiming
                ? t('profile.predictionWinners.claiming') || 'CLAIMING...'
                : t('profile.actions.claimRefund')}
          </Button>
        )}
        {status !== 'ongoing' &&
          status !== 'resolving' &&
          status !== 'canceled' && (
            <Button
              onClick={onViewWinners}
              className={cn(
                'w-full rounded-t-none border-0 active:border-0',
                isClaimWin || isClaimed || isClaimRefund || isClaimedRefund
                  ? 'rounded-bl-none'
                  : ''
              )}
              variant="secondary"
            >
              {t('profile.actions.viewWinners')}
            </Button>
          )}
      </div>
    </div>
  );
}

export default TransferHistoryCard;
